import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { createBrowserClient } from '@supabase/ssr';
import type { User, Session } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

// One client per runtime. In the browser this must be the auth-aware SSR client;
// creating a second supabase-js client with the same storage key triggers multiple
// GoTrue instances and can race session updates.
let supabaseClient: SupabaseClient | null = null;

function getSupabaseClient(): SupabaseClient | null {
  if (supabaseClient) return supabaseClient;

  if (!supabaseUrl || !supabaseAnonKey) {
    // Silently skip in development/build if not configured
    return null;
  }

  supabaseClient = typeof window === 'undefined'
    ? createClient(supabaseUrl, supabaseAnonKey)
    : createBrowserClient(supabaseUrl, supabaseAnonKey);
  return supabaseClient;
}

export const supabase = getSupabaseClient();

export function getSupabaseBrowserClient(): SupabaseClient | null {
  if (typeof window === 'undefined') return null;
  return getSupabaseClient();
}

// Re-export auth types for convenience
export type { User, Session };

// Types for our tables
export interface AnonymousUser {
  device_id: string;
  created_at: string;
  last_seen_at: string;
  total_generations: number;
}

export type FeedbackAction = 'kept' | 'edited' | 'deleted' | 'thumbs_up' | 'thumbs_down';

export interface UserFeedback {
  id: string;
  device_id: string;
  card_id: string;
  action: FeedbackAction;
  edit_diff: Record<string, unknown> | null;
  created_at: string;
}

export type CardType = 'cloze' | 'basic' | 'image_occlusion';

export interface GeneratedCard {
  card_id: string;
  device_id: string;
  card_type: CardType;
  front: string | null;
  back: string | null;
  cloze_text: string | null;
  source_context: string | null;
  created_at: string;
}

// Saved deck for premium users (cloud storage)
export interface SavedDeck {
  id: string;
  user_id: string;
  file_name: string;
  cards: unknown[]; // JSON array of FlashCard objects
  card_count: number;
  session_id?: string | null; // For loading images from cache
  original_file_url?: string | null; // URL to stored PDF/PPTX for regeneration/download
  created_at: string;
  updated_at: string;
}

export type SavedDeckSummary = Omit<SavedDeck, 'cards'> & { folder_id?: string | null };

// Testimonial from users
export interface Testimonial {
  id: string;
  device_id: string | null;
  user_id: string | null;
  email: string | null;
  testimonial_text: string;
  rating: number | null;
  approved: boolean;
  public_permission: boolean;
  display_name: string | null;
  student_context: string | null;
  source_job_id: string | null;
  created_at: string;
}
